var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import 'overlayscrollbars/styles/overlayscrollbars.css';
import cn from 'classnames';
import { OverlayScrollbars } from 'overlayscrollbars';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useLayoutEffect } from '@snack-uikit/utils';
import { AUTOSCROLL_ENABLE_LIMIT, AUTOSCROLL_TO, BAR_AUTO_HIDE_DELAY_MS, BAR_HIDE_STRATEGY, RESIZE, SIZE, } from '../constants';
import styles from './styles.module.css';
/**
 * Установить nonce атрибут для инлайн стилей.
 * @function setNonce
 */
export const setNonce = OverlayScrollbars.nonce;
export const Scroll = forwardRef(function Scroll(_a, ref) {
    var { children, className, onScroll: onScrollProp, onInitialized: onInitializedProp, size = SIZE.M, resize = RESIZE.None, clickScrolling = true, barHideStrategy = BAR_HIDE_STRATEGY.Leave, autoscrollTo, paddingAbsolute, untouchableScrollbars = false } = _a, rest = __rest(_a, ["children", "className", "onScroll", "onInitialized", "size", "resize", "clickScrolling", "barHideStrategy", "autoscrollTo", "paddingAbsolute", "untouchableScrollbars"]);
    const overlayScrollbarsRef = useRef(null);
    const [isOverlayScrollbarInited, setOverlayScrollbarInited] = useState(false);
    const getOSInstance = useCallback((overlayScrollbars = overlayScrollbarsRef.current) => overlayScrollbars === null || overlayScrollbars === void 0 ? void 0 : overlayScrollbars.osInstance(), [overlayScrollbarsRef]);
    useImperativeHandle(ref, () => { var _a, _b, _c; return ((_c = (_b = (_a = overlayScrollbarsRef.current) === null || _a === void 0 ? void 0 : _a.osInstance()) === null || _b === void 0 ? void 0 : _b.elements().viewport) !== null && _c !== void 0 ? _c : null); });
    const lastPositionsRef = useRef({ scrolledToBottom: false, scrolledToRight: false });
    const syncPositions = useCallback((scrollbars) => {
        const instance = getOSInstance(scrollbars);
        if (instance) {
            const { viewport } = instance.elements();
            const gapToBottom = viewport.scrollHeight - (viewport.offsetHeight + viewport.scrollTop);
            const gapToRight = viewport.scrollWidth - (viewport.offsetWidth + viewport.scrollLeft);
            const hasYScroll = viewport.scrollHeight > viewport.offsetHeight;
            const hasXScroll = viewport.scrollWidth > viewport.offsetWidth;
            lastPositionsRef.current.scrolledToBottom = gapToBottom < AUTOSCROLL_ENABLE_LIMIT || !hasYScroll;
            lastPositionsRef.current.scrolledToRight = gapToRight < AUTOSCROLL_ENABLE_LIMIT || !hasXScroll;
        }
    }, [getOSInstance]);
    useLayoutEffect(() => {
        if (!autoscrollTo) {
            onInitializedProp === null || onInitializedProp === void 0 ? void 0 : onInitializedProp();
            return;
        }
        const instance = getOSInstance();
        if (instance && isOverlayScrollbarInited) {
            syncPositions();
            const { content } = instance.elements();
            autoscrollTo === AUTOSCROLL_TO.Bottom && content.scroll(0, content.scrollHeight);
            autoscrollTo === AUTOSCROLL_TO.Right && content.scroll(content.scrollWidth, 0);
        }
        onInitializedProp === null || onInitializedProp === void 0 ? void 0 : onInitializedProp();
        /* здесь умышленно autoscrollTo не входит в депсы хука, потому что его значение интересно только на момент маунта,
        но в дальнейшем не должно триггерить эту функцию */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getOSInstance, isOverlayScrollbarInited, syncPositions]);
    const onInitialized = useCallback(() => {
        setOverlayScrollbarInited(true);
    }, []);
    const onScroll = useCallback((event) => {
        onScrollProp === null || onScrollProp === void 0 ? void 0 : onScrollProp(event);
        if (autoscrollTo) {
            syncPositions();
        }
    }, 
    /* autoscrollTo не аффектит на расчеты в syncPositions */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onScrollProp, syncPositions]);
    const onContentSizeChanged = useCallback(() => {
        var _a;
        if (!autoscrollTo)
            return;
        const instance = (_a = getOSInstance()) === null || _a === void 0 ? void 0 : _a.elements();
        if (instance === null || instance === void 0 ? void 0 : instance.content) {
            if (lastPositionsRef.current.scrolledToBottom && autoscrollTo === AUTOSCROLL_TO.Bottom) {
                instance === null || instance === void 0 ? void 0 : instance.content.scroll(0, instance === null || instance === void 0 ? void 0 : instance.content.scrollHeight);
            }
            if (lastPositionsRef.current.scrolledToRight && autoscrollTo === AUTOSCROLL_TO.Right) {
                instance === null || instance === void 0 ? void 0 : instance.content.scroll(instance === null || instance === void 0 ? void 0 : instance.content.scrollWidth, 0);
            }
        }
    }, [getOSInstance, autoscrollTo]);
    return (_jsx(OverlayScrollbarsComponent, Object.assign({}, rest, { ref: overlayScrollbarsRef, "data-size": size, "data-untouchable-scrollbars": untouchableScrollbars || undefined, className: cn(className, styles.scroll, 'osThemeSnack'), style: { resize }, options: {
            paddingAbsolute,
            scrollbars: {
                autoHide: barHideStrategy,
                autoHideDelay: BAR_AUTO_HIDE_DELAY_MS,
                clickScroll: clickScrolling,
            },
        }, events: {
            initialized: onInitialized,
            scroll: (_instance, event) => {
                onScroll(event);
            },
            updated: onContentSizeChanged,
        }, children: autoscrollTo ? isOverlayScrollbarInited && children : children })));
});
